import { useEffect, useRef, useState } from "react";
import { BlinkDetector } from "../../utils/eyeBlinkDetection";
import { getFaceLandmarker } from "../../utils/faceLandmarker";

function buildStatus({ loading, error, faceDetected, blinkVerified, blinkCount, requiredBlinks }) {
  if (loading) return "Loading face detection…";
  if (error) return error;
  if (!faceDetected) return "Center your face in the frame";
  if (blinkVerified) return "Blink verified — tap Capture";
  if (blinkCount > 0 && blinkCount < requiredBlinks) {
    return `Blink again (${blinkCount}/${requiredBlinks})`;
  }
  return "Blink once to continue";
}

export function useEyeBlinkLiveness({
  active = false,
  videoRef,
  requiredBlinks = 1,
  resetKey = 0,
}) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [faceDetected, setFaceDetected] = useState(false);
  const [blinkCount, setBlinkCount] = useState(0);
  const [blinkVerified, setBlinkVerified] = useState(false);

  const landmarkerRef = useRef(null);
  const detectorRef = useRef(null);
  const frameRef = useRef(null);
  const lastVideoTimeRef = useRef(-1);

  useEffect(() => {
    detectorRef.current = new BlinkDetector(requiredBlinks);
  }, [requiredBlinks]);

  useEffect(() => {
    detectorRef.current?.reset();
    setFaceDetected(false);
    setBlinkCount(0);
    setBlinkVerified(false);
    lastVideoTimeRef.current = -1;
  }, [resetKey]);

  useEffect(() => {
    if (!active) {
      setError("");
      if (frameRef.current) {
        cancelAnimationFrame(frameRef.current);
        frameRef.current = null;
      }
      return undefined;
    }

    detectorRef.current?.reset();
    setFaceDetected(false);
    setBlinkCount(0);
    setBlinkVerified(false);
    lastVideoTimeRef.current = -1;

    let cancelled = false;
    setLoading(true);
    setError("");

    (async () => {
      try {
        landmarkerRef.current = await getFaceLandmarker();
        if (!cancelled) setLoading(false);
      } catch {
        if (!cancelled) {
          setLoading(false);
          setError("Face detection could not start. Try again or upload a photo.");
        }
      }
    })();

    return () => {
      cancelled = true;
      if (frameRef.current) {
        cancelAnimationFrame(frameRef.current);
        frameRef.current = null;
      }
    };
  }, [active]);

  useEffect(() => {
    if (!active || loading || error || !landmarkerRef.current) {
      return undefined;
    }

    const detect = () => {
      const video = videoRef?.current;
      const landmarker = landmarkerRef.current;
      if (!video || !landmarker || video.readyState < HTMLMediaElement.HAVE_CURRENT_DATA) {
        frameRef.current = requestAnimationFrame(detect);
        return;
      }

      if (video.currentTime === lastVideoTimeRef.current) {
        frameRef.current = requestAnimationFrame(detect);
        return;
      }
      lastVideoTimeRef.current = video.currentTime;

      try {
        const result = landmarker.detectForVideo(video, performance.now());
        const landmarks = result.faceLandmarks?.[0];
        const snapshot = detectorRef.current.update(landmarks);
        setFaceDetected(snapshot.faceDetected);
        setBlinkCount(snapshot.blinkCount);
        setBlinkVerified(snapshot.blinkVerified);
      } catch {
        // Skip frame on transient detection errors.
      }

      frameRef.current = requestAnimationFrame(detect);
    };

    frameRef.current = requestAnimationFrame(detect);

    return () => {
      if (frameRef.current) {
        cancelAnimationFrame(frameRef.current);
        frameRef.current = null;
      }
    };
  }, [active, loading, error, videoRef]);

  const statusText = buildStatus({
    loading,
    error,
    faceDetected,
    blinkVerified,
    blinkCount,
    requiredBlinks,
  });

  const canCapture = active && !loading && !error && faceDetected && blinkVerified;

  return {
    loading,
    error,
    faceDetected,
    blinkCount,
    blinkVerified,
    statusText,
    canCapture,
  };
}
